// Renderer-side source of worker-carried ANTLR parse errors for the mssql live
// syntax-diagnostics pass (plan20 T2 × plan16 wedge fix).
//
// WHY THIS EXISTS: plan20 T2's first cut ran `collectTSqlParseErrors` — a full
// ANTLR parse — on the renderer MAIN THREAD per debounce tick (measured 16.5 s
// of main-thread self-time on the 67 KB fixture in the built app; the C2 e2e
// wedge). The parse now runs inside the shared T-SQL parse worker
// (one-parse-two-products with the procedural-scope side table —
// `parseWorkerSyntax.ts`); this module is the thin client carrying the
// semantics the diagnostics pass needs:
//
//   - EVENTUALLY CONSISTENT, not deadline-raced: syntax markers may land
//     seconds late on a cold bundle load. There is deliberately NO reply
//     deadline and NO inline fallback (either would put the ANTLR parse right
//     back on the main thread). Stale application is prevented by the CALLER —
//     SqlMonaco stamps the model version and drops replies for edited buffers.
//   - TRAILING COALESCE, one outstanding request: the worker is single-
//     threaded, so posting every debounce tick behind a slow cold parse would
//     re-create the duplicate-job storm the plan16 bridge fixed for scope
//     requests. At most ONE syntax request is in flight; while it runs, only
//     the LATEST waiting source survives — superseded callers resolve `null`
//     ("no verdict"; the newer edit already scheduled its own pass).
//   - HARD-CAP RESPAWN (F2): a request pinned past `SYNTAX_HARD_CAP_MS`
//     terminates + respawns the shared worker via `respawnParseWorker` so a
//     genuinely hung worker doesn't wedge scope AND syntax for the session.
//   - FAILURE LATCH: a real worker failure (script error / error reply) turns
//     the pass off for the session — resolve `[]` once so markers CLEAR (the
//     safe direction), then `null` thereafter. No respawn storms. A respawn-
//     class rejection (deliberate reset) does NOT latch.

import {
  isWorkerRespawnError,
  requestSyntaxErrors,
  respawnParseWorker,
} from '@/features/sql-grammar-tsql/parseWorkerClient';
import type { TSqlParseError } from '@/features/sql-grammar-tsql/model';

/** `null` = "no verdict for this request" (superseded by a newer edit, or the
 *  pass is latched off after already painting `[]`) — the caller leaves the
 *  currently-painted markers alone. An array (possibly empty) is a real
 *  verdict for exactly the requested `source`. */
export type WorkerSyntaxResult = readonly TSqlParseError[] | null;

/** Mirrors the bridge's `INFLIGHT_HARD_CAP_MS` rationale — sized well above
 *  the worst measured cold full-file parse in the packaged app (17.2 s). */
export const SYNTAX_HARD_CAP_MS = 60_000;

interface SyntaxWorkerStats {
  /** Worker replies consumed (verdicts delivered to the diagnostics pass). */
  workerReplies: number;
  /** Callers superseded by a newer edit while a request was in flight. */
  superseded: number;
  /** Oversize refusals (buffer above the worker's full-parse cap). */
  oversize: number;
  /** Non-respawn worker failures (script error / error reply). */
  failures: number;
  lastError: string | null;
  /** True once a failure latched the pass off for the session. */
  latched: boolean;
  /** Hard-cap expiries that terminated + respawned the shared worker (F2). */
  hardCapRespawns: number;
}

/** Always-on diagnostic counters (no PII, low cardinality) — the syntax-path
 *  sibling of `__jamSqlParseWorkerStats__`, letting the built-app e2e prove
 *  the WORKER (not a main-thread parse) produced the markers. */
const stats: SyntaxWorkerStats = {
  workerReplies: 0,
  superseded: 0,
  oversize: 0,
  failures: 0,
  lastError: null,
  latched: false,
  hardCapRespawns: 0,
};
(globalThis as unknown as { __jamSqlSyntaxWorkerStats__?: SyntaxWorkerStats }).__jamSqlSyntaxWorkerStats__ =
  stats;

let latchedOff = false;
let pumping = false;
/** The single surviving waiter — a newer call replaces (and null-resolves) it. */
let queued: { source: string; resolve: (r: WorkerSyntaxResult) => void } | null = null;

/** Test seam — reset latch/queue/stats between cases. Keeps the SAME `stats`
 *  object reference on `globalThis` valid across resets. */
export function __resetWorkerSyntaxSourceForTest(): void {
  latchedOff = false;
  queued = null;
  stats.workerReplies = 0;
  stats.superseded = 0;
  stats.oversize = 0;
  stats.failures = 0;
  stats.lastError = null;
  stats.latched = false;
  stats.hardCapRespawns = 0;
}

/**
 * Resolve the raw ANTLR parse errors for `source` via the parse worker.
 * NEVER throws and NEVER parses on the main thread — the worst case is a
 * `null` (no verdict) or `[]` (clear markers) result.
 */
export function getWorkerSyntaxParseErrors(source: string): Promise<WorkerSyntaxResult> {
  if (latchedOff) return Promise.resolve(null);
  // Empty/whitespace buffers have no syntax errors by definition — answer
  // locally rather than waking (or spawning) the worker.
  if (!source.trim()) return Promise.resolve([]);
  return new Promise<WorkerSyntaxResult>((resolve) => {
    if (queued) {
      stats.superseded += 1;
      queued.resolve(null);
    }
    queued = { source, resolve };
    void pump();
  });
}

async function pump(): Promise<void> {
  if (pumping) return;
  pumping = true;
  try {
    while (queued) {
      const job = queued;
      queued = null;
      job.resolve(await runJob(job.source));
    }
  } finally {
    pumping = false;
  }
}

const HARD_CAP = Symbol('syntax-hard-cap');

async function runJob(source: string): Promise<WorkerSyntaxResult> {
  try {
    const reply = await withHardCap(requestSyntaxErrors({ source }));
    stats.workerReplies += 1;
    if (reply.oversize) {
      // No verdict is derivable for an oversize buffer — clear (empty marker
      // set) rather than leave squiggles we can no longer re-check.
      stats.oversize += 1;
      return [];
    }
    // `?? []` keeps a partially-malformed reply from throwing (degradation-safe).
    return reply.parseErrors ?? [];
  } catch (err) {
    if (err === HARD_CAP) {
      // F2: the worker is almost certainly hung — terminate + leave it for
      // lazy respawn so the NEXT request (scope or syntax) gets a fresh one.
      // No latch: the respawned worker may be perfectly healthy.
      stats.hardCapRespawns += 1;
      respawnParseWorker('syntax request exceeded SYNTAX_HARD_CAP_MS');
      return [];
    }
    if (isWorkerRespawnError(err)) {
      // Deliberate hard reset fired elsewhere (e.g. the scope bridge's hard
      // cap) — not evidence the worker is unavailable. Clear and retry on the
      // next edit; no latch.
      return [];
    }
    stats.failures += 1;
    stats.lastError = err instanceof Error ? err.message : String(err);
    latchedOff = true;
    stats.latched = true;
    return [];
  }
}

/** Race a promise against the hard cap. On expiry the returned promise rejects
 *  with the `HARD_CAP` sentinel; the underlying request's eventual settle is
 *  absorbed by the already-settled race (no unhandled rejection). */
function withHardCap<T>(p: Promise<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(HARD_CAP), SYNTAX_HARD_CAP_MS);
    p.then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      (e) => {
        clearTimeout(timer);
        reject(e);
      },
    );
  });
}
